import axios from 'axios';
import { API_BASE_URL } from '../config';

/**
 * Upload Service - API Layer
 * Handles all file upload operations with proper error handling
 */

/**
 * Upload a document for an incident report
 * @param {File} file - The file to upload
 * @param {number|string} reportId - The incident report ID (can be draft ID)
 * @param {Function} onProgress - Callback for upload progress (0-100)
 * @returns {Promise} Response with uploaded file data
 */
export const uploadDocument = async (file, reportId, onProgress = null) => {
  const formData = new FormData();
  formData.append('document', file);
  
  if (reportId) {
    formData.append('reportId', reportId);
  }

  const token = localStorage.getItem('token');
  
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/incident-reports/documents/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

/**
 * Delete a document from an incident report
 * @param {string} documentPath - The document path to delete
 * @param {number|string} reportId - The incident report ID
 * @returns {Promise} Response confirming deletion
 */
export const deleteDocument = async (documentPath, reportId) => {
  const token = localStorage.getItem('token');
  
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/incident-reports/documents`,
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        data: { documentPath, reportId }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Delete document error:', error);
    throw error;
  }
};

/**
 * Get document URL for preview/download
 * @param {string} documentPath - The document path
 * @returns {string} Full URL to the document
 */
export const getDocumentUrl = (documentPath) => {
  if (!documentPath) return '';
  
  // If it's already a full URL, return it
  if (documentPath.startsWith('http')) {
    return documentPath;
  }
  
  // Otherwise, construct the full URL
  return `${API_BASE_URL}/${documentPath}`;
};

/**
 * Validate file before upload
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Object} { valid: boolean, error: string }
 */
export const validateFile = (file, options = {}) => {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'text/plain'
    ]
  } = options;

  // Check file size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds ${formatFileSize(maxSize)} limit`
    };
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type not allowed. Accepted types: PDF, Word, Excel, Images, Text`
    };
  }

  return { valid: true, error: null };
};

/**
 * Format file size to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Get file extension from filename
 * @param {string} filename - The filename
 * @returns {string} File extension
 */
export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

/**
 * Get file icon based on file type
 * @param {string} fileType - MIME type or extension
 * @returns {string} Icon name for react-icons
 */
export const getFileIcon = (fileType) => {
  if (fileType.includes('pdf')) return 'FaFilePdf';
  if (fileType.includes('word') || fileType.includes('document')) return 'FaFileWord';
  if (fileType.includes('excel') || fileType.includes('sheet')) return 'FaFileExcel';
  if (fileType.includes('image')) return 'FaFileImage';
  if (fileType.includes('text')) return 'FaFileAlt';
  return 'FaFile';
};
